import { useState } from "react";
import { Loader2, Send, Copy, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";

const SUB_KEY = "mt_newsletter_subscribed_v1";
export const NEWSLETTER_SUB_KEY = SUB_KEY;
// Posé dès l'envoi du courriel de vérification (double opt-in) : le popup de
// bienvenue ne se répète plus pendant que la confirmation est en attente.
const PENDING_KEY = "mt_newsletter_pending_v1";
export const NEWSLETTER_PENDING_KEY = PENDING_KEY;

// Inscription à la lettre d'information (infos et spéciaux) en DOUBLE OPT-IN :
// la personne reçoit un courriel avec un lien de vérification ; le code WELCOME10
// n'est envoyé par courriel qu'APRÈS le clic de confirmation (jamais affiché à l'écran).
export default function NewsletterForm({ source = "popup", testid = "newsletter", onSubscribed }) {
  const { t, ts, lang } = useLang();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);  // courriel de vérification envoyé
  const [code, setCode] = useState(localStorage.getItem(SUB_KEY) || "");

  const submit = async () => {
    if (busy) return;
    const value = email.trim();
    // Le bouton reste toujours cliquable : on valide ici (l'autofill navigateur ne
    // déclenche pas toujours onChange, un bouton désactivé semblait alors « cassé »).
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
      setError(t("promo.newsletter.invalid"));
      return;
    }
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { email: value, lang, source });
      if (data.pending) {
        localStorage.setItem(PENDING_KEY, "1");  // vérification en attente : ne plus relancer le popup
        setPending(true);  // double opt-in : vérification par courriel requise
      } else if (data.code) {
        localStorage.setItem(SUB_KEY, data.code);
        setCode(data.code);  // abonné déjà vérifié : code renvoyé par courriel
      }
      toast.success(t("promo.newsletter.success_title"));
      onSubscribed?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("promo.newsletter.invalid"));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(t("promo.popup_copied", { code }));
    } catch { /* presse-papiers indisponible */ }
  };

  if (pending) {
    return (
      <div data-testid={`${testid}-pending`} className="border border-gold/30 bg-gold/5 rounded-xl p-4 text-center">
        <MailCheck className="w-6 h-6 text-gold mx-auto mb-2" />
        <p className="text-sm font-semibold mb-1">{t("promo.newsletter.success_title")}</p>
        <p className="text-xs text-ivory/75">{t("promo.newsletter.success_body")}</p>
      </div>
    );
  }

  if (code) {
    return (
      <div data-testid={`${testid}-done`} className="border border-gold/30 bg-gold/5 rounded-xl p-4 text-center">
        <MailCheck className="w-6 h-6 text-gold mx-auto mb-2" />
        <p className="text-sm font-semibold mb-1">{t("promo.newsletter.code_title")}</p>
        <p className="text-xs text-ivory/75 mb-3">{t("promo.newsletter.code_body")}</p>
        <button onClick={copy} data-testid={`${testid}-copy`}
          className="inline-flex items-center gap-2 border border-gold/50 text-gold font-bold text-sm tracking-wider px-5 py-2.5 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300">
          <Copy className="w-3.5 h-3.5" /> {code}
        </button>
      </div>
    );
  }

  return (
    <div data-testid={`${testid}-form`}>
      <div className="flex flex-col gap-2.5">
        <input
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setError(""); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
          placeholder={ts("promo.newsletter.placeholder")}
          data-testid={`${testid}-email`}
          className="w-full bg-ink border border-ivory/15 rounded-full px-4 py-3 text-sm focus:border-gold focus:outline-none transition-colors duration-300"
        />
        {error && <p data-testid={`${testid}-error`} className="text-xs text-red-400 text-center">{error}</p>}
        <button onClick={submit} disabled={busy} data-testid={`${testid}-submit`}
          className="btn-gold-shine w-full inline-flex items-center justify-center gap-2 text-ink font-bold px-5 py-3 rounded-full text-sm transition-shadow duration-300 disabled:opacity-70 disabled:cursor-wait">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {t("promo.newsletter.cta")}
        </button>
      </div>
      <p className="text-[11px] text-ivory/65 mt-2 text-center">{t("promo.newsletter.hint")}</p>
    </div>
  );
}
